// frontend/src/components/EditStockTransfer.jsx
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import {
  Package, Warehouse, Building, Calendar, Hash, DollarSign,
  ClipboardList, Percent, Trash2, User, Truck,
  FileText, Calculator, Save, X, Clock, AlertCircle,
  Loader2, RefreshCw, ArrowLeftRight, AlertTriangle,
  ArrowUp, ArrowDown, Download, MapPin,
  Plus, Layers, TrendingUp, TrendingDown, Send
} from "lucide-react";
import * as XLSX from "xlsx";
import { useAuth } from "../../contexts/AuthContext";
import "./StockTransfer.css";

const url = process.env.REACT_APP_BACKEND_URL;

const EditStockTransfer = () => {
  const { id } = useParams();
  const { currentUser, userType, company } = useAuth();
  const navigate = useNavigate();
  
  // State
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stockTransfer, setStockTransfer] = useState(null);
  
  // Dropdown data
  const [products, setProducts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [productStock, setProductStock] = useState({});
  const [transactionTypes, setTransactionTypes] = useState([]);
  
  // Loading states
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [sourceWarehouseStock, setSourceWarehouseStock] = useState({});

  // Form state
  const [formData, setFormData] = useState({
    from_warehouse_id: "",
    to_warehouse_id: "",
    transfer_date: new Date().toISOString().split('T')[0],
    notes: "",
  });

  // Hidden fields that will be set automatically
  const [transferStatus, setTransferStatus] = useState("pending");

  // Transfer items array
  const [transferItems, setTransferItems] = useState([]);

  // Focus states
  const [focusedFields, setFocusedFields] = useState({});
  const [errors, setErrors] = useState({});

  // Totals
  const [totals, setTotals] = useState({
    totalItems: 0,
    totalQuantity: 0,
    totalValue: 0,
  });

  const isSystemAdmin = userType === 'system' && (currentUser?.role === 'developer' || currentUser?.role === 'admin');
  
  // FIX: Allow editing for all statuses EXCEPT cancelled
  const isTransferCancelled = stockTransfer?.status === 'cancelled';
  const isTransferEditable = !isTransferCancelled;

  // Get company ID
  const getCompanyId = () => {
    if (stockTransfer) {
      return stockTransfer.company_id?._id || stockTransfer.company_id;
    }
    if (userType === 'company' && company) {
      return company._id || company;
    }
    return null;
  };

  // Fetch source warehouse stock
  const fetchSourceWarehouseStock = async (warehouseId, productId, index) => {
    if (!warehouseId || !productId) return;

    try {
      const companyId = getCompanyId();
      
      const response = await axios.get(`${url}/api/inventorystock/warehouse/${warehouseId}`, {
        params: {
          company_id: companyId,
          product_id: productId,
          status: 'active'
        }
      });
      
      if (response.data.success) {
        const stockItem = response.data.data.find(
          item => item.product_id?._id === productId || item.product_id === productId
        );
        
        if (stockItem) {
          setSourceWarehouseStock(prev => ({
            ...prev,
            [warehouseId]: {
              [productId]: {
                current_stock: stockItem.quantity || 0,
                available_stock: stockItem.available_qty || 0,
                avg_cost: stockItem.avg_cost || 0
              }
            }
          }));
          
          // Auto-populate unit cost if available and not already set
          if (stockItem.avg_cost && stockItem.avg_cost > 0 && !transferItems[index]?.unit_cost) {
            const updated = [...transferItems];
            updated[index].unit_cost = stockItem.avg_cost.toFixed(2);
            calculateItemTotal(index, updated[index].quantity, stockItem.avg_cost);
            setTransferItems(updated);
          }
        }
      }
    } catch (error) {
      // Silent fail
    }
  };

  // Fetch products for warehouse
  const fetchProductsForWarehouse = async (warehouseId) => {
    if (!warehouseId) {
      setFilteredProducts(products);
      setProductStock({});
      return;
    }

    try {
      setLoadingProducts(true);
      const companyId = getCompanyId();
      
      const response = await axios.get(`${url}/api/inventorystock/warehouse/${warehouseId}`, {
        params: {
          company_id: companyId,
          status: 'active'
        }
      });
      
      if (response.data.success) {
        const stockMap = {};
        const warehouseProducts = (response.data.data || []).map(stock => {
          const product = stock.product_id || {};
          stockMap[product._id] = {
            current_stock: stock.quantity || 0,
            available_stock: stock.available_qty || 0,
            avg_cost: stock.avg_cost || 0
          };
          return {
            _id: product._id,
            product_id: product._id,
            product_code: product.product_code || 'N/A',
            product_name: product.product_name || 'Unknown',
            current_stock: stock.quantity || 0,
            available_stock: stock.available_qty || 0,
            avg_cost: stock.avg_cost || 0
          };
        });
        
        setProductStock(stockMap);
        setFilteredProducts(warehouseProducts);
      } else {
        setFilteredProducts(products);
        setProductStock({});
      }
    } catch (error) {
      setFilteredProducts(products);
      setProductStock({});
    } finally {
      setLoadingProducts(false);
    }
  };

  // Handle from warehouse change
  const handleFromWarehouseChange = (index, warehouseId) => {
    const updated = [...transferItems];
    updated[index].from_warehouse_id = warehouseId;
    updated[index].product_id = "";
    updated[index].quantity = "";
    updated[index].unit_cost = "";
    updated[index].total_cost = "0";
    setTransferItems(updated);
    
    if (warehouseId) {
      fetchProductsForWarehouse(warehouseId);
    }
  };

  // Handle product change
  const handleProductChange = (index, productId) => {
    const updated = [...transferItems];
    updated[index].product_id = productId;
    setTransferItems(updated);

    const warehouseId = updated[index].from_warehouse_id;
    if (warehouseId && productId) {
      fetchSourceWarehouseStock(warehouseId, productId, index);
    }
  };

  // Calculate item total - always round to 2 decimal places
  const calculateItemTotal = (index, quantity, unitCost) => {
    const updated = [...transferItems];
    const qty = parseFloat(quantity) || 0;
    const cost = parseFloat(unitCost) || 0;
    const total = (qty * cost).toFixed(2);
    updated[index].total_cost = total;
    setTransferItems(updated);
  };

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch transfer details
        const transferRes = await axios.get(`${url}/api/stocktransfers/${id}`, {
          timeout: 10000
        });

        const transfer = transferRes.data.data;
        setStockTransfer(transfer);
        setTransferStatus(transfer.status || "pending");

        // Get company ID for dropdowns
        const companyId = transfer.company_id?._id || transfer.company_id;

        // Fetch dropdown data
        await fetchAllDropdownData(companyId);

        // Set form data
        setFormData({
          from_warehouse_id: transfer.from_warehouse_id?._id || transfer.from_warehouse_id || "",
          to_warehouse_id: transfer.to_warehouse_id?._id || transfer.to_warehouse_id || "",
          transfer_date: transfer.transfer_date 
            ? new Date(transfer.transfer_date).toISOString().split('T')[0] 
            : new Date().toISOString().split('T')[0],
          notes: transfer.notes || "",
        });

        // Set transfer items data
        setTransferItems([{
          product_id: transfer.product_id?._id || transfer.product_id || "",
          from_warehouse_id: transfer.from_warehouse_id?._id || transfer.from_warehouse_id || "",
          to_warehouse_id: transfer.to_warehouse_id?._id || transfer.to_warehouse_id || "",
          quantity: transfer.quantity?.toString() || "",
          unit_cost: transfer.unit_cost ? parseFloat(transfer.unit_cost).toFixed(2) : "",
          total_cost: transfer.total_cost?.toFixed(2) || "0",
          batch_number: transfer.batch_number || "",
          expiry_date: transfer.expiry_date 
            ? new Date(transfer.expiry_date).toISOString().split('T')[0] 
            : "",
          description: transfer.description || "",
        }]);

        // Fetch products for warehouse if from warehouse is selected
        if (transfer.from_warehouse_id?._id || transfer.from_warehouse_id) {
          const warehouseId = transfer.from_warehouse_id?._id || transfer.from_warehouse_id;
          await fetchProductsForWarehouse(warehouseId);
          
          if (transfer.product_id?._id || transfer.product_id) {
            const productId = transfer.product_id?._id || transfer.product_id;
            setTimeout(() => {
              fetchSourceWarehouseStock(warehouseId, productId, 0);
            }, 500);
          }
        }

      } catch (error) {
        setError(error.message);
        
        let errorMsg = "Failed to load stock transfer";
        if (error.response) {
          errorMsg = error.response.data?.message || errorMsg;
          if (error.response.status === 404) {
            errorMsg = "Stock transfer not found";
          }
        }
        
        toast.error(errorMsg);
      } finally {
        setLoading(false);
      }
    };

    if (currentUser && id) {
      fetchData();
    }
  }, [currentUser, id, navigate]);

  // Fetch all dropdown data
  const fetchAllDropdownData = async (companyId) => {
    if (!companyId) return;

    try {
      const params = { company_id: companyId, status: 'active' };

      // Fetch warehouses
      try {
        const whRes = await axios.get(`${url}/api/warehouse/dropdown`, { params });
        if (whRes.data?.success) {
          setWarehouses(whRes.data.data || []);
        }
      } catch (err) {
        setWarehouses([]);
      }

      // Fetch products
      try {
        const prodRes = await axios.get(`${url}/api/products/dropdown`, { params });
        if (prodRes.data?.success) {
          const productData = prodRes.data.data || [];
          setProducts(productData);
          setFilteredProducts(productData);
        }
      } catch (err) {
        setProducts([]);
        setFilteredProducts([]);
      }

      // Fetch transaction types (transfer_in and transfer_out)
      try {
        const ttRes = await axios.get(`${url}/api/transactiontype/dropdown`, { params });
        if (ttRes.data?.success) {
          const allTypes = ttRes.data.data || [];
          
          const transferTypes = allTypes.filter(type => {
            const typeName = (type.transaction_type || '').toLowerCase();
            return typeName === 'transfer_in' || 
                   typeName === 'transfer_out' ||
                   typeName.includes('transfer');
          });
          
          setTransactionTypes(transferTypes);
        }
      } catch (err) {
        // Silent fail
      }

    } catch (error) {
      // Silent fail
    }
  };

  // Handle warehouse change
  const handleWarehouseChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (name === 'from_warehouse_id') {
      if (value) {
        fetchProductsForWarehouse(value);
        
        const updated = transferItems.map(item => ({
          ...item,
          from_warehouse_id: value,
          product_id: "",
          quantity: "",
          unit_cost: "",
          total_cost: "0"
        }));
        setTransferItems(updated);
      } else {
        setFilteredProducts(products);
        setProductStock({});
      }
    }
    
    if (name === 'to_warehouse_id') {
      const updated = transferItems.map(item => ({
        ...item,
        to_warehouse_id: value
      }));
      setTransferItems(updated);
    }

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  // Calculate totals
  useEffect(() => {
    let totalQty = 0;
    let totalVal = 0;
    let itemCount = 0;

    transferItems.forEach(item => {
      const qty = parseFloat(item.quantity) || 0;
      const val = parseFloat(item.total_cost) || 0;
      
      if (item.product_id && qty > 0) {
        totalQty += qty;
        totalVal += val;
        itemCount++;
      }
    });

    setTotals({
      totalItems: itemCount,
      totalQuantity: totalQty,
      totalValue: totalVal,
    });
  }, [transferItems]);

  // Calculate item total
  const calculateProductTotal = (item) => {
    const qty = parseFloat(item.quantity) || 0;
    const cost = parseFloat(item.unit_cost) || 0;
    return (qty * cost).toFixed(2);
  };

  // ========== TRANSFER ITEM HANDLERS ==========
  const addTransferItem = () => {
    setTransferItems([...transferItems, {
      product_id: "",
      from_warehouse_id: formData.from_warehouse_id,
      to_warehouse_id: formData.to_warehouse_id,
      quantity: "",
      unit_cost: "",
      total_cost: "0",
      batch_number: "",
      expiry_date: "",
      description: "",
    }]);
  };

  const removeTransferItem = (index) => {
    if (transferItems.length > 1) {
      setTransferItems(transferItems.filter((_, i) => i !== index));
    } else {
      toast.warning("At least one transfer item is required");
    }
  };

  // ========== FORM HANDLERS ==========
  const handleFocus = (field) =>
    setFocusedFields((prev) => ({ ...prev, [field]: true }));

  const handleBlur = (field) => {
    const value = formData[field];
    setFocusedFields((prev) => ({
      ...prev,
      [field]: (typeof value === 'string' ? value.trim() : value) ? true : false
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'from_warehouse_id' || name === 'to_warehouse_id') {
      handleWarehouseChange(e);
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  // Enhanced item change handler with decimal validation
  const handleItemChange = (index, field, value) => {
    const updated = [...transferItems];
    
    if (field === 'from_warehouse_id') {
      handleFromWarehouseChange(index, value);
    } else if (field === 'product_id') {
      handleProductChange(index, value);
    } else if (field === 'quantity') {
      if (value === '' || /^\d+$/.test(value)) {
        updated[index][field] = value;
        calculateItemTotal(index, value, updated[index].unit_cost);
        setTransferItems(updated);
      }
    } else if (field === 'unit_cost') {
      if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
        updated[index][field] = value;
        calculateItemTotal(index, updated[index].quantity, value);
        setTransferItems(updated);
      }
    } else {
      updated[index][field] = value;
      setTransferItems(updated);
    }

    if (errors[`${field}_${index}`]) {
      setErrors(prev => ({ ...prev, [`${field}_${index}`]: "" }));
    }
  };

  // Format unit cost on blur to ensure 2 decimal places
  const handleUnitCostBlur = (index) => {
    const updated = [...transferItems];
    const value = updated[index].unit_cost;
    if (value && value !== '') {
      const numValue = parseFloat(value);
      if (!isNaN(numValue)) {
        updated[index].unit_cost = numValue.toFixed(2);
        calculateItemTotal(index, updated[index].quantity, numValue);
        setTransferItems(updated);
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.from_warehouse_id) {
      newErrors.from_warehouse_id = "Source warehouse is required";
    }
    if (!formData.to_warehouse_id) {
      newErrors.to_warehouse_id = "Destination warehouse is required";
    }
    if (!formData.transfer_date) {
      newErrors.transfer_date = "Transfer date is required";
    }

    if (formData.from_warehouse_id && formData.to_warehouse_id && 
        formData.from_warehouse_id === formData.to_warehouse_id) {
      newErrors.warehouse_match = "Source and destination warehouses must be different";
    }

    const validItems = transferItems.filter(item =>
      item.product_id &&
      item.quantity &&
      parseFloat(item.quantity) > 0
    );

    if (validItems.length === 0) {
      newErrors.items = "At least one valid transfer item is required";
    } else {
      transferItems.forEach((item, index) => {
        if (item.from_warehouse_id && item.to_warehouse_id && 
            item.from_warehouse_id === item.to_warehouse_id) {
          newErrors[`warehouse_${index}`] = "Source and destination warehouses must be different";
        }

        if (item.product_id && item.from_warehouse_id) {
          const qty = parseFloat(item.quantity) || 0;
          const stockInfo = sourceWarehouseStock[item.from_warehouse_id]?.[item.product_id];
          
          if (stockInfo && stockInfo.current_stock < qty) {
            newErrors[`stock_${index}`] = `Insufficient stock. Available: ${stockInfo.current_stock}`;
          }
        }

        if (item.product_id && (!item.quantity || parseFloat(item.quantity) <= 0)) {
          newErrors[`quantity_${index}`] = "Quantity must be greater than 0";
        }
        
        if (item.unit_cost && item.unit_cost.includes('.') && item.unit_cost.split('.')[1].length > 2) {
          newErrors[`unit_cost_${index}`] = "Unit cost cannot have more than 2 decimal places";
        }
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ========== TEMPLATE DOWNLOAD ==========
  const downloadTemplate = () => {
    const templateData = [
      {
        "Product Code": "PROD001",
        "Product Name": "Sample Product",
        "From Warehouse Code": "WH001",
        "To Warehouse Code": "WH002",
        "Quantity": 100,
        "Unit Cost": 25.50,
        "Batch No": "BATCH001",
        "Expiry Date": new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        "Transfer Date": new Date().toISOString().split('T')[0],
        "Description": "Stock transfer between warehouses",
      }
    ];
  
    const worksheet = XLSX.utils.json_to_sheet(templateData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Stock Transfers");
    
    const wscols = [
      { wch: 15 },
      { wch: 25 },
      { wch: 15 },
      { wch: 15 },
      { wch: 10 },
      { wch: 12 },
      { wch: 15 },
      { wch: 15 },
      { wch: 15 },
      { wch: 30 }
    ];
    worksheet["!cols"] = wscols;
  
    XLSX.writeFile(workbook, `Stock-Transfer-Template.xlsx`);
    toast.info("Template downloaded successfully!");
  };

  // ========== CANCEL HANDLER ==========
  const handleCancel = () => {
    navigate("/ViewStockTransfer");
  };

  // ========== SUBMIT HANDLER ==========
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fix form errors");
      return;
    }

    if (stockTransfer) {
      const originalQty = stockTransfer.quantity;
      const newQty = parseFloat(transferItems[0]?.quantity) || 0;
      
      if (originalQty !== newQty) {
        const confirm = window.confirm(
          `Changing quantity from ${originalQty} to ${newQty} will adjust inventory levels. Continue?`
        );
        if (!confirm) return;
      }
    }

    setSubmitting(true);
    try {
      const validItems = transferItems.filter(item =>
        item.product_id &&
        item.quantity &&
        parseFloat(item.quantity) > 0
      );

      const payload = {
        from_warehouse_id: formData.from_warehouse_id,
        to_warehouse_id: formData.to_warehouse_id,
        transfer_date: formData.transfer_date,
        notes: formData.notes,
        description: validItems[0]?.description || formData.notes,
        
        product_id: validItems[0].product_id,
        quantity: parseInt(validItems[0].quantity, 10),
        unit_cost: parseFloat(parseFloat(validItems[0].unit_cost || 0).toFixed(2)),
        batch_number: validItems[0].batch_number || "",
        expiry_date: validItems[0].expiry_date || null,
        
        update_inventory: true
      };

      const res = await axios.put(`${url}/api/stocktransfers/${id}`, payload, {
        headers: {
          "Content-Type": "application/json"
        },
      });

      if (res.data.success) {
        toast.success("Stock transfer updated successfully!");
        
        setTimeout(() => {
          navigate("/ViewStockTransfer");
        }, 1500);
      }
    } catch (error) {
      let errorMsg = "Failed to update stock transfer";
      
      if (error.response) {
        errorMsg = error.response.data?.message || errorMsg;
        
        if (error.response.data?.message?.includes('stock') || 
            error.response.data?.message?.includes('Insufficient')) {
          errorMsg = error.response.data.message;
        }
        
        if (error.response.data?.errors) {
          const serverErrors = {};
          
          if (Array.isArray(error.response.data.errors)) {
            error.response.data.errors.forEach(err => {
              if (err.field) {
                serverErrors[err.field] = err.message;
              }
            });
          } else if (typeof error.response.data.errors === 'object') {
            Object.keys(error.response.data.errors).forEach(key => {
              serverErrors[key] = error.response.data.errors[key];
            });
          }
          
          setErrors(serverErrors);
        }
      }
      
      toast.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (amount) => {
    const absAmount = Math.abs(amount || 0);
    return '$' + absAmount.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,');
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return "Invalid date";
    }
  };

  const getStatusDisplayInfo = () => {
    if (transferStatus === 'completed') return { text: 'Completed', color: '#52c41a', bg: '#f6ffed' };
    if (transferStatus === 'cancelled') return { text: 'Cancelled', color: '#ff4d4f', bg: '#fff2f0' };
    return { text: 'Pending', color: '#856404', bg: '#fff3cd' };
  };

  if (loading) {
    return (
      <div className="form-wrapper">
        <div className="transfer-form-container">
          <div className="loading-container">
            <Loader2 className="animate-spin" size={32} />
            <p>Loading stock transfer data...</p>
            <p style={{ fontSize: '12px', color: '#999' }}>Please wait...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !stockTransfer) {
    return (
      <div className="form-wrapper">
        <div className="transfer-form-container">
          <div className="error-container">
            <AlertCircle size={48} color="#dc3545" />
            <h2>Transfer Not Found</h2>
            <p>{error || "Stock transfer not found"}</p>
            <button 
              className="cancel-btn" 
              onClick={handleCancel}
              style={{ marginTop: '20px' }}
            >
              Back to List
            </button>
          </div>
        </div>
      </div>
    );
  }

  const statusDisplay = getStatusDisplayInfo();

  return (
    <div className="form-wrapper">
      <div className="transfer-form-container">
        <button
          className="close-btn"
          onClick={handleCancel}
          title="Go Back"
          type="button"
        >
          <X size={20} />
        </button>

        <div className="form-header">
          <ArrowLeftRight size={24} />
          <h2 className="form-title">
            Edit Stock Transfer
          </h2>
          <p className="form-subtitle">
            Update transfer details for {stockTransfer.reference_no}
          </p>
        </div>

        {isTransferCancelled && (
          <div style={{
            padding: '12px 20px',
            borderRadius: '8px',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            background: '#fff2f0',
            border: '1px solid #ffccc7',
            color: '#ff4d4f'
          }}>
            <AlertTriangle size={20} />
            <span style={{ fontWeight: 600 }}>
              CANCELLED TRANSFER - Cannot be edited
            </span>
          </div>
        )}

        {transactionTypes.length > 0 && (
          <div style={{ 
            background: '#f0f5ff', 
            padding: '12px 16px', 
            borderRadius: '8px', 
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            border: '1px solid #d6e4ff'
          }}>
            <ArrowLeftRight size={18} color="#2f54eb" />
            <div style={{ fontSize: '13px' }}>
              <span style={{ fontWeight: 600, color: '#2f54eb' }}>Transaction Types:</span>{' '}
              {transactionTypes.map((type, idx) => (
                <span key={type._id}>
                  {idx > 0 && ' | '}
                  <span style={{ 
                    background: type.sign === '+' ? '#f6ffed' : '#fff2f0',
                    padding: '2px 8px',
                    borderRadius: '12px',
                    color: type.sign === '+' ? '#52c41a' : '#ff4d4f',
                    margin: '0 4px'
                  }}>
                    {type.transaction_type} ({type.sign})
                  </span>
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Transfer Info Banner */}
        <div style={{
          background: '#f0f9ff',
          border: '1px solid #bae7ff',
          borderRadius: '8px',
          padding: '16px 20px',
          marginBottom: '20px',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '12px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Hash size={14} color="#1890ff" />
            <span style={{ fontSize: '13px' }}>
              <strong>Reference No:</strong> {stockTransfer.reference_no || "N/A"}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FileText size={14} color="#1890ff" />
            <span style={{ fontSize: '13px' }}>
              <strong>Transfer ID:</strong> #{stockTransfer.transfer_id || "N/A"}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Building size={14} color="#1890ff" />
            <span style={{ fontSize: '13px' }}>
              <strong>Company:</strong> {stockTransfer.company_id?.company_name || stockTransfer.company_id}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <User size={14} color="#1890ff" />
            <span style={{ fontSize: '13px' }}>
              <strong>Created:</strong> {formatDate(stockTransfer.created_at)}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Clock size={14} color="#1890ff" />
            <span style={{ fontSize: '13px' }}>
              <strong>Status:</strong> 
              <span style={{ 
                marginLeft: '4px',
                color: statusDisplay.color,
                fontWeight: 600
              }}>
                {statusDisplay.text}
              </span>
            </span>
          </div>
          {stockTransfer.description && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', gridColumn: '1 / -1' }}>
              <FileText size={14} color="#1890ff" />
              <span style={{ fontSize: '13px' }}>
                <strong>Description:</strong> {stockTransfer.description}
              </span>
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit}>
          {/* Basic Info */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px', marginBottom: '20px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: 5, fontWeight: 500 }}>From Warehouse *</label>
              <select
                name="from_warehouse_id"
                value={formData.from_warehouse_id}
                onChange={handleChange}
                required
                style={{ 
                  width: '100%', 
                  padding: 8, 
                  borderRadius: 4, 
                  border: '1px solid #ddd',
                  backgroundColor: !isTransferEditable ? '#f5f5f5' : 'white',
                  cursor: !isTransferEditable ? 'not-allowed' : 'pointer'
                }}
                disabled={warehouses.length === 0 || !isTransferEditable}
              >
                <option value="">
                  {warehouses.length === 0 ? "No warehouses available" : "Select Source Warehouse *"}
                </option>
                {warehouses.map((warehouse) => (
                  <option key={warehouse._id} value={warehouse._id}>
                    {warehouse.warehouse_code} - {warehouse.warehouse_name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 5, fontWeight: 500 }}>To Warehouse *</label>
              <select
                name="to_warehouse_id"
                value={formData.to_warehouse_id}
                onChange={handleChange}
                required
                style={{ 
                  width: '100%', 
                  padding: 8, 
                  borderRadius: 4, 
                  border: '1px solid #ddd',
                  backgroundColor: !isTransferEditable ? '#f5f5f5' : 'white',
                  cursor: !isTransferEditable ? 'not-allowed' : 'pointer'
                }}
                disabled={warehouses.length === 0 || !isTransferEditable}
              >
                <option value="">
                  {warehouses.length === 0 ? "No warehouses available" : "Select Destination Warehouse *"}
                </option>
                {warehouses.map((warehouse) => (
                  <option key={warehouse._id} value={warehouse._id}>
                    {warehouse.warehouse_code} - {warehouse.warehouse_name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 5, fontWeight: 500 }}>Transfer Date *</label>
              <input
                type="date"
                name="transfer_date"
                value={formData.transfer_date}
                onChange={handleChange}
                required
                style={{ 
                  width: '100%', 
                  padding: 8, 
                  borderRadius: 4, 
                  border: '1px solid #ddd',
                  backgroundColor: !isTransferEditable ? '#f5f5f5' : 'white',
                  cursor: !isTransferEditable ? 'not-allowed' : 'pointer'
                }}
                disabled={!isTransferEditable}
              />
            </div>
          </div>

          {errors.warehouse_match && (
            <div style={{ 
              gridColumn: 'span 3', 
              color: '#ff4d4f', 
              fontSize: '13px', 
              marginTop: '-10px', 
              marginBottom: '15px',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}>
              <AlertCircle size={12} />
              <span>{errors.warehouse_match}</span>
            </div>
          )}

          {/* Transfer Items Section */}
          <h3 style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 15 }}>
            <Package size={18} /> Transfer Items
            {formData.from_warehouse_id && (
              <span style={{ fontSize: 13, color: '#666', fontWeight: 'normal', marginLeft: '10px' }}>
                (Showing stock for source warehouse)
              </span>
            )}
          </h3>

          {errors.items && (
            <div style={{ 
              marginBottom: '15px', 
              padding: '10px', 
              background: '#fff2f0', 
              border: '1px solid #ffccc7',
              borderRadius: '4px',
              color: '#ff4d4f',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <AlertCircle size={16} />
              <span>{errors.items}</span>
            </div>
          )}

          {transferItems.map((item, index) => (
            <div key={index} style={{
              marginBottom: '20px',
              padding: '20px',
              background: '#fafafa',
              borderRadius: '8px',
              border: '1px solid #f0f0f0',
              position: 'relative'
            }}>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr', gap: '10px' }}>
                <div>
                  <label style={{ fontSize: 12, color: '#666' }}>Product *</label>
                  <select
                    value={item.product_id}
                    onChange={(e) => handleItemChange(index, 'product_id', e.target.value)}
                    required
                    style={{ 
                      width: '100%', 
                      padding: 8, 
                      borderRadius: 4, 
                      border: '1px solid #ddd',
                      backgroundColor: (!formData.from_warehouse_id || loadingProducts || !isTransferEditable) ? '#f5f5f5' : 'white',
                      cursor: (!formData.from_warehouse_id || loadingProducts || !isTransferEditable) ? 'not-allowed' : 'pointer'
                    }}
                    disabled={!formData.from_warehouse_id || loadingProducts || !isTransferEditable}
                  >
                    <option value="">
                      {!formData.from_warehouse_id ? "Select source warehouse first" :
                       loadingProducts ? "Loading products..." :
                       filteredProducts.length === 0 ? "No products available" : "Select Product *"}
                    </option>
                    {filteredProducts.map((prod) => (
                      <option key={prod._id} value={prod._id}>
                        {prod.product_name} ({prod.product_code})
                        {productStock[prod._id] && ` - Stock: ${productStock[prod._id].current_stock}`}
                      </option>
                    ))}
                  </select>
                  {item.product_id && sourceWarehouseStock[item.from_warehouse_id]?.[item.product_id] && (
                    <div style={{ 
                      fontSize: 11, 
                      marginTop: 4, 
                      padding: '4px 8px',
                      borderRadius: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      background: sourceWarehouseStock[item.from_warehouse_id][item.product_id].current_stock < (parseFloat(item.quantity) || 0) 
                        ? '#fff2f0' 
                        : '#f6ffed',
                      border: `1px solid ${
                        sourceWarehouseStock[item.from_warehouse_id][item.product_id].current_stock < (parseFloat(item.quantity) || 0) 
                          ? '#ffccc7' 
                          : '#b7eb8f'
                      }`,
                      color: sourceWarehouseStock[item.from_warehouse_id][item.product_id].current_stock < (parseFloat(item.quantity) || 0) 
                        ? '#ff4d4f' 
                        : '#52c41a'
                    }}>
                      <Layers size={12} />
                      <span>
                        Available in source: <strong>
                          {sourceWarehouseStock[item.from_warehouse_id][item.product_id].current_stock}
                        </strong> units
                        {sourceWarehouseStock[item.from_warehouse_id][item.product_id].avg_cost > 0 && (
                          <> | Avg Cost: ${sourceWarehouseStock[item.from_warehouse_id][item.product_id].avg_cost.toFixed(2)}</>
                        )}
                        {sourceWarehouseStock[item.from_warehouse_id][item.product_id].current_stock < (parseFloat(item.quantity) || 0) && (
                          <span style={{ marginLeft: '4px', fontWeight: 600 }}> ⚠️ Insufficient stock!</span>
                        )}
                      </span>
                    </div>
                  )}
                  {errors[`stock_${index}`] && (
                    <div style={{ 
                      fontSize: 11, 
                      marginTop: 4, 
                      color: '#ff4d4f',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}>
                      <AlertCircle size={12} />
                      <span>{errors[`stock_${index}`]}</span>
                    </div>
                  )}
                </div>

                <div>
                  <label style={{ fontSize: 12, color: '#666' }}>Quantity *</label>
                  <input
                    type="number"
                    value={item.quantity}
                    onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                    placeholder="0"
                    min="1"
                    step="1"
                    required
                    style={{ 
                      width: '100%', 
                      padding: 8, 
                      borderRadius: 4, 
                      border: '1px solid #ddd',
                      backgroundColor: !isTransferEditable ? '#f5f5f5' : 'white',
                      cursor: !isTransferEditable ? 'not-allowed' : 'pointer'
                    }}
                    disabled={!isTransferEditable}
                    onKeyDown={(e) => {
                      if (e.key === '.') {
                        e.preventDefault();
                      }
                    }}
                  />
                  <small style={{ color: '#999', fontSize: '10px' }}>Whole numbers only</small>
                </div>

                <div>
                  <label style={{ fontSize: 12, color: '#666' }}>Unit Cost</label>
                  <input
                    type="number"
                    value={item.unit_cost}
                    onChange={(e) => handleItemChange(index, 'unit_cost', e.target.value)}
                    onBlur={() => handleUnitCostBlur(index)}
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                    style={{ 
                      width: '100%', 
                      padding: 8, 
                      borderRadius: 4, 
                      border: '1px solid #ddd',
                      backgroundColor: !isTransferEditable ? '#f5f5f5' : 'white',
                      cursor: !isTransferEditable ? 'not-allowed' : 'pointer'
                    }}
                    disabled={!isTransferEditable}
                  />
                  <small style={{ color: '#999', fontSize: '10px' }}>Max 2 decimal places</small>
                </div>

                <div>
                  <label style={{ fontSize: 12, color: '#666' }}>Total</label>
                  <input
                    type="text"
                    value={formatCurrency(parseFloat(item.total_cost) || 0)}
                    readOnly
                    style={{ 
                      width: '100%', 
                      padding: 8, 
                      borderRadius: 4, 
                      border: '1px solid #ddd', 
                      background: '#f5f5f5', 
                      fontWeight: 600, 
                      color: '#1890ff' 
                    }}
                  />
                </div>

                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <div style={{
                    padding: '4px 8px',
                    background: '#e6f7ff',
                    borderRadius: '4px',
                    fontSize: '11px',
                    color: '#1890ff',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}>
                    <TrendingUp size={12} />
                    <span>OUT → IN</span>
                    <TrendingDown size={12} />
                  </div>
                </div>
              </div>

              {errors[`warehouse_${index}`] && (
                <div style={{ 
                  marginTop: '5px', 
                  marginBottom: '5px', 
                  padding: '5px 8px',
                  background: '#fff2f0',
                  border: '1px solid #ffccc7',
                  borderRadius: '4px',
                  color: '#ff4d4f',
                  fontSize: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  <AlertCircle size={12} />
                  <span>{errors[`warehouse_${index}`]}</span>
                </div>
              )}
              
              {errors[`unit_cost_${index}`] && (
                <div style={{ 
                  marginTop: '5px', 
                  marginBottom: '5px', 
                  padding: '5px 8px',
                  background: '#fff2f0',
                  border: '1px solid #ffccc7',
                  borderRadius: '4px',
                  color: '#ff4d4f',
                  fontSize: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  <AlertCircle size={12} />
                  <span>{errors[`unit_cost_${index}`]}</span>
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 10 }}>
                <div>
                  <input
                    type="text"
                    placeholder="Batch Number (optional)"
                    value={item.batch_number}
                    onChange={(e) => handleItemChange(index, 'batch_number', e.target.value)}
                    style={{ 
                      width: '100%', 
                      padding: 8, 
                      borderRadius: 4, 
                      border: '1px solid #ddd',
                      backgroundColor: !isTransferEditable ? '#f5f5f5' : 'white',
                      cursor: !isTransferEditable ? 'not-allowed' : 'pointer'
                    }}
                    disabled={!isTransferEditable}
                  />
                </div>
                <div>
                  <input
                    type="date"
                    placeholder="Expiry Date"
                    value={item.expiry_date}
                    onChange={(e) => handleItemChange(index, 'expiry_date', e.target.value)}
                    style={{ 
                      width: '100%', 
                      padding: 8, 
                      borderRadius: 4, 
                      border: '1px solid #ddd',
                      backgroundColor: !isTransferEditable ? '#f5f5f5' : 'white',
                      cursor: !isTransferEditable ? 'not-allowed' : 'pointer'
                    }}
                    disabled={!isTransferEditable}
                  />
                </div>
              </div>

              <div style={{ marginTop: 10 }}>
                <input
                  type="text"
                  placeholder="Item specific notes (optional)"
                  value={item.description}
                  onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                  style={{ 
                    width: '100%', 
                    padding: 8, 
                    borderRadius: 4, 
                    border: '1px solid #ddd',
                    backgroundColor: !isTransferEditable ? '#f5f5f5' : 'white',
                    cursor: !isTransferEditable ? 'not-allowed' : 'pointer'
                  }}
                  disabled={!isTransferEditable}
                />
              </div>

              {index > 0 && isTransferEditable && (
                <button
                  type="button"
                  onClick={() => removeTransferItem(index)}
                  style={{
                    position: 'absolute',
                    top: '10px',
                    right: '10px',
                    padding: '4px 8px',
                    background: '#fff1f0',
                    border: '1px solid #ffccc7',
                    borderRadius: '4px',
                    color: '#ff4d4f',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4
                  }}
                  title="Remove Item"
                >
                  <Trash2 size={14} /> Remove
                </button>
              )}
            </div>
          ))}

          {isTransferEditable && (
            <div style={{ marginBottom: '30px' }}>
              <button
                type="button"
                onClick={addTransferItem}
                style={{
                  padding: '10px 20px',
                  background: 'transparent',
                  border: '2px dashed #667eea',
                  borderRadius: '6px',
                  color: '#667eea',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '14px',
                  fontWeight: 600
                }}
                disabled={!formData.from_warehouse_id || !formData.to_warehouse_id}
              >
                <Plus size={16} /> Add Another Item
              </button>
              {(!formData.from_warehouse_id || !formData.to_warehouse_id) && (
                <small style={{ display: 'block', marginTop: '5px', color: '#fa8c16' }}>
                  Please select source and destination warehouses first to add items
                </small>
              )}
            </div>
          )}

          <div style={{ marginBottom: '20px' }}>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              placeholder="Additional notes for this transfer..."
              rows="3"
              style={{ 
                width: '100%', 
                padding: '12px', 
                borderRadius: 4, 
                border: '1px solid #ddd',
                backgroundColor: !isTransferEditable ? '#f5f5f5' : 'white',
                cursor: !isTransferEditable ? 'not-allowed' : 'pointer'
              }}
              disabled={!isTransferEditable}
            />
          </div>

          <h3 style={{ marginBottom: 15 }}>Transfer Summary</h3>
          <div style={{
            background: '#fafafa',
            padding: '20px',
            borderRadius: '8px',
            border: '1px solid #f0f0f0',
            marginBottom: '20px'
          }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px', textAlign: 'center' }}>
              <div>
                <div style={{ fontSize: '13px', color: '#666', marginBottom: '5px' }}>Total Items</div>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1890ff' }}>
                  {totals.totalItems}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '13px', color: '#666', marginBottom: '5px' }}>Total Quantity</div>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#52c41a' }}>
                  {totals.totalQuantity.toLocaleString()}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '13px', color: '#666', marginBottom: '5px' }}>Total Value</div>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#722ed1' }}>
                  {formatCurrency(totals.totalValue)}
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '15px', marginTop: '30px' }}>
            <button
              type="button"
              onClick={handleCancel}
              disabled={submitting}
              style={{
                padding: '12px 24px',
                background: 'white',
                border: '1px solid #d9d9d9',
                borderRadius: '6px',
                color: '#666',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '14px'
              }}
            >
              <X size={16} /> Cancel
            </button>

            {isTransferEditable ? (
              <button
                type="submit"
                disabled={submitting}
                style={{
                  padding: '12px 24px',
                  background: '#1890ff',
                  border: 'none',
                  borderRadius: '6px',
                  color: 'white',
                  cursor: submitting ? 'wait' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '14px',
                  fontWeight: 600,
                  opacity: submitting ? 0.7 : 1
                }}
              >
                {submitting ? (
                  <>
                    <Loader2 className="animate-spin" size={16} />
                    Updating Transfer...
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    Update Transfer
                  </>
                )}
              </button>
            ) : (
              <div style={{
                padding: '12px 24px',
                background: '#f5f5f5',
                borderRadius: '6px',
                color: '#999',
                fontSize: '14px',
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <Clock size={16} />
                <span>{isTransferCancelled ? 'Transfer Cancelled' : 'Transfer Completed'}</span>
              </div>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditStockTransfer;